/**
 * <s-stitching-form> keeps custom-stitching measurements while the customer switches
 * colour (the product form re-renders on every variant change) by saving them for the
 * browser tab and restoring them when the form reconnects.
 */
class StitchingForm extends HTMLElement {
  #ac;

  get #key() {
    return `et-stitching-${this.dataset.productId || ''}`;
  }

  connectedCallback() {
    this.#ac = new AbortController();
    this.#restore();
    this.#applyMode();
    this.addEventListener('input', () => this.#save(), { signal: this.#ac.signal });
    this.addEventListener(
      'change',
      (event) => {
        this.#applyMode();
        this.#save();
        const target = /** @type {HTMLElement} */ (event.target);
        if (target instanceof HTMLInputElement && target.dataset.stitchField === 'mode') this.#scrollToStart();
      },
      { signal: this.#ac.signal },
    );
  }

  /** "Send size later" hides and disables the measurement fields so they are neither required nor submitted. */
  #applyMode() {
    const later = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-stitch-mode="later"]'))?.checked ?? false;
    const area = /** @type {HTMLElement | null} */ (this.querySelector('[data-stitch-measurements]'));
    const note = /** @type {HTMLElement | null} */ (this.querySelector('[data-stitch-later-note]'));
    if (area) {
      area.hidden = later;
      for (const field of /** @type {NodeListOf<HTMLInputElement | HTMLSelectElement>} */ (area.querySelectorAll('input, select, textarea'))) {
        field.disabled = later;
      }
    }
    if (note) note.hidden = !later;
    for (const el of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll("[data-stitch-now-only]"))) {
      el.hidden = later;
    }
  }

  /** After choosing now/later, bring the top of the "Your custom measurements" box back into view, just below the header. */
  #scrollToStart() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const header = document.querySelector('header');
    const subBar = document.querySelector('[data-subheader-inner]'); const offset = Math.max((header ? header.getBoundingClientRect().bottom : 0) + (subBar ? subBar.offsetHeight : 0) + 16, 16); // leave room for the menu bar, which slides back in when scrolling up
    window.scrollTo({ top: this.getBoundingClientRect().top + window.scrollY - offset, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #fields() {
    return /** @type {NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>} */ (
      this.querySelectorAll('[data-stitch-field]')
    );
  }

  #save() {
    /** @type {Record<string, string>} */
    const values = {};
    for (const field of this.#fields()) {
      const key = field.dataset.stitchField || '';
      if (field instanceof HTMLInputElement && field.type === 'radio') {
        if (field.checked) values[key] = field.value;
      } else {
        values[key] = field.value;
      }
    }
    try {
      sessionStorage.setItem(this.#key, JSON.stringify(values));
    } catch {
      /* storage unavailable */
    }
  }

  #restore() {
    let values = null;
    try {
      values = JSON.parse(sessionStorage.getItem(this.#key) || 'null');
    } catch {
      values = null;
    }
    if (!values) return;
    for (const field of this.#fields()) {
      const saved = values[field.dataset.stitchField || ''];
      if (saved === undefined) continue;
      if (field instanceof HTMLInputElement && field.type === 'radio') {
        field.checked = field.value === saved;
      } else {
        field.value = saved;
      }
    }
  }
}

if (!customElements.get('s-stitching-form')) {
  customElements.define('s-stitching-form', StitchingForm);
}
